// src/components/personal/sections/DashboardSection/components/DashboardMetrics.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, MetricCard  } from '@/components/ui/Layout';
import { TEXT_STYLES } from '@/theme';
import { 
  formatNumber, 
  formatPercent, 
  formatPrice 
} from '@/utils/Formatters';
import { NUMBER_FORMATS, LOCALES } from '@/utils/constants';
import { DashboardMetrics as DashboardMetricsType, MetricType } from '../../types/types';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';

interface DashboardMetricsProps {
  metrics: DashboardMetricsType | null;
  onSelectMetric: (metric: MetricType) => void;
  selectedMetric: MetricType;
}

const DashboardMetrics: React.FC<DashboardMetricsProps> = ({ 
  metrics, 
  onSelectMetric,
  selectedMetric
}) => {
  const { t, i18n } = useTranslation('dashboard');
  const { currencySymbol } = useBusinessCurrency();
  const currentLocale = i18n.language || LOCALES.DEFAULT;
  
  // Если метрики не переданы, не рендерим компонент
  if (!metrics) return null;
  
  // Функция для форматирования значения в зависимости от типа метрики
  const formatMetricValue = (type: MetricType, value: number): string => {
    if (type === 'conversion') {
      return formatPercent(value / 100, NUMBER_FORMATS.PERCENT_PRECISION, true, currentLocale);
    } else if (type === 'revenue' || type === 'averageCheck') {
      return formatPrice(value, currencySymbol, currentLocale);
    }
    // Для остальных типов (orders, clients) используем форматирование чисел
    return formatNumber(value, NUMBER_FORMATS.DEFAULT_PRECISION, true, currentLocale);
  };
  
  return (
    <div>
      {/* Заголовок с метриками */}
      <div className="bg-[#F5FAF5] px-5 py-3 rounded-t-lg border border-[#e5e7eb]">
        <h2 className={TEXT_STYLES.h2}>{t('metrics.title')}</h2>
      </div>
      
      {/* Карточки с метриками */}
      <div className="bg-white px-5 py-4 rounded-b-lg border-x border-b border-[#e5e7eb]">
        <Grid 
          cols={5} 
          mobileCols={2} 
          tabletCols={2} 
          gap="md"
          className="metric-grid"
        >
          {Object.entries(metrics).map(([key, metric]) => {
            const metricKey = key as MetricType;
            
            // Безопасное получение значения
            const value = metric && typeof metric.value !== 'undefined' ? metric.value : 0;
            const change = metric && typeof metric.change !== 'undefined' ? metric.change : 0;
            
            // Определяем направление тренда
            let trendDirection: 'up' | 'down' | 'neutral' = 'neutral';
            if (metric?.trend === 'up') {
              trendDirection = 'up';
            } else if (metric?.trend === 'down') {
              trendDirection = 'down';
            }
            
            return (
              <MetricCard
                key={key}
                title={t(`metrics.${key}`)}
                value={formatMetricValue(metricKey, value)}
                trend={trendDirection}
                trendValue={change}
                isSelected={selectedMetric === metricKey}
                onClick={() => onSelectMetric(metricKey)}
              />
            );
          })}
        </Grid>
      </div>
    </div>
  );
};

export default DashboardMetrics;